import { action, computed, makeObservable, observable, runInAction } from "mobx";
import {
  Model,
  Command,
  getVotingDetails,
  sendVote,
  ApiVotingDetails,
  ConsentsType,
  VoteType,
  VotingViewQuestion,
  ToastType,
  VOTING_STATUSES,
  GENERAL_MAIL_REGEX,
  MAX_MAIL_SIGNS,
  getFormattedDate,
  FORM_ERROR_MESSAGE,
  isLoggedIn,
  getOrCreateZkSecret,
  zkCommitment,
  proveZkVote,
  isSnarkjsReady,
  getZkVoteMaterial,
  submitZkVote,
  ZkMaterial
} from "@lubbezposrednio-frontend/core";
import { NavigateFunction } from "react-router-dom";
import { startCyberidVerify, readCyberidAssertion } from "../../utils/cyberidAuth";

const HAS_ALREADY_VOTED_MESSAGE = "has already been taken";

export class VotingViewModel extends Model {
  id?: string;
  isPublic?: boolean;
  organizer?: { name: string; imageUrl?: string; profileRef?: string | null };
  name?: string;
  startDate?: string;
  endDate?: string;
  questions?: VotingViewQuestion[] = [];
  email = "";
  consents: ConsentsType = {} as ConsentsType;
  consentsError = "";
  questionErrors: { questionId: number; message: string }[] = [];
  emailError = "";
  isLoading = false;
  isEmailNecessary = false;
  accessDenied = false; // zalogowany bez uprawnień (403) → czysty komunikat, nie „goły" input e-mail
  status?: VOTING_STATUSES;
  electorateType?: string;
  signupLandingId?: string | null;
  signedUp = false;
  voteConfirmation = "email";
  accountEligible = false;
  accountVoted = false;
  accountEmail = "";
  requiresVerification = false;
  accountRequired = false;
  accountVerified = false;
  isOwner = false;
  secret?: boolean;
  walletPolicy?: string;           // none|recommended|required (Faza D)
  showWalletHandoff = false;       // tajne + wymagany portfel → ceremonia w aplikacji (enklawa)
  startDateRaw = "";
  endDateRaw = "";
  signupsCount = 0;
  questionsPreview: { content: string; qtype: string; answers: string[] }[] = [];
  privacyPolicyUrl = "";
  termsAndConditionsUrl = "";
  verifiedIdentityRequired = false;   // głosowanie wymaga zweryfikowanej tożsamości CyberID (bez konta)
  minAssuranceLevel = 2;              // minimalny poziom Aury (A2 = dokument/mDowód)
  cyberidAssertion?: string;          // asercja weryfikacji (po powrocie z CyberID) — dołączana do głosu
  cyberidEmail = "";                  // e-mail potwierdzony przez CyberID

  constructor() {
    super();

    makeObservable(this, {
      id: observable,
      isPublic: observable,
      email: observable,
      handleEmailChange: action.bound,
      organizer: observable.ref,
      name: observable,
      startDate: observable,
      endDate: observable,
      questions: observable,
      consents: observable.ref,
      privacyPolicyUrl: observable,
      termsAndConditionsUrl: observable,
      isLoading: observable,
      isEmailNecessary: observable,
      accessDenied: observable,
      status: observable,
      electorateType: observable,
      signupLandingId: observable,
      signedUp: observable,
      voteConfirmation: observable,
      accountEligible: observable,
      accountVoted: observable,
      accountEmail: observable,
      requiresVerification: observable,
      accountRequired: observable,
      accountVerified: observable,
      isOwner: observable,
      secret: observable,
      walletPolicy: observable,
      showWalletHandoff: observable,
      startDateRaw: observable,
      endDateRaw: observable,
      signupsCount: observable,
      questionsPreview: observable.ref,
      changeConsent: action.bound,
      saveVotingDetails: action.bound,
      selectAnswer: action.bound,
      setAbstain: action.bound,
      consentsError: observable,
      validateConsents: action.bound,
      questionErrors: observable.ref,
      validateQuestions: action.bound,
      emailError: observable,
      validateEmail: action.bound,
      verifiedIdentityRequired: observable,
      minAssuranceLevel: observable,
      cyberidAssertion: observable,
      cyberidEmail: observable,
      cyberidVerified: computed,
      startVerification: action.bound,
      loadCyberidAssertion: action.bound,
      isError: computed,
      clearData: action.bound
    });
  }

  public changeConsent(consent: Partial<ConsentsType>) {
    this.consents = { ...this.consents, ...consent };
    this.consentsError && this.validateConsents();
  }

  public getQuestionError(questionId: number) {
    return this.questionErrors.find(error => error.questionId === questionId)?.message;
  }

  public handleEmailChange(email: string) {
    this.email = email;
    this.emailError && this.validateEmail();
  }

  public selectAnswer(questionId: number, answerId: number) {
    const question = this.questions?.find(question => question.id === questionId);
    if (question) {
      question.abstained = false;
      const ids = question.selectedAnswerIds ?? [];
      if (question.qtype === "multiple") {
        const max = question.max_selections ?? Infinity;
        if (ids.includes(answerId)) {
          question.selectedAnswerIds = ids.filter(id => id !== answerId);
        } else if (ids.length < max) {
          question.selectedAnswerIds = [...ids, answerId];
        }
      } else {
        // single / yes_no — pojedynczy wybór (klik w zaznaczony = odznacz)
        question.selectedAnswerIds = ids.includes(answerId) ? [] : [answerId];
      }
    }

    this.getQuestionError(questionId) && this.validateQuestions();
  }

  public setAbstain(questionId: number) {
    const question = this.questions?.find(question => question.id === questionId);
    if (question) {
      question.abstained = !question.abstained;
      if (question.abstained) question.selectedAnswerIds = [];
    }

    this.getQuestionError(questionId) && this.validateQuestions();
  }

  private getFormattedDate(date: string) {
    return getFormattedDate(new Date(date));
  }

  saveVotingDetails({ voting }: ApiVotingDetails) {
    const { user } = voting;

    const userName =
      (user.first_name || user.last_name) && `${voting.user.first_name ?? ""} ${voting.user.last_name ?? ""}`;
    const organizerName = user.is_organization ? user.organization_name : userName;

    this.isPublic = voting.public;
    this.organizer = {
      name: organizerName ?? user.email,
      imageUrl: user.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : undefined,
      profileRef: (user as { profile_ref?: string | null }).profile_ref ?? null
    };
    this.name = voting.title;
    this.startDate = this.getFormattedDate(voting.start_date);
    this.endDate = this.getFormattedDate(voting.end_date);
    this.startDateRaw = voting.start_date;
    this.endDateRaw = voting.end_date;
    this.questions = voting.questions;
    this.status = voting.status;
    this.electorateType = (voting as { electorate_type?: string }).electorate_type;
    this.signupLandingId = (voting as { signup_landing_id?: string | null }).signup_landing_id;
    this.signedUp = (voting as { signed_up?: boolean }).signed_up ?? false;
    this.voteConfirmation = (voting as { vote_confirmation?: string }).vote_confirmation ?? "email";
    this.accountEligible = (voting as { account_eligible?: boolean }).account_eligible ?? false;
    this.accountVoted = (voting as { account_voted?: boolean }).account_voted ?? false;
    this.accountEmail = (voting as { account_email?: string | null }).account_email ?? "";
    if (this.accountEligible && this.accountEmail) {
      this.email = this.accountEmail;
    }
    this.requiresVerification = (voting as { requires_verification?: boolean }).requires_verification ?? false;
    this.accountRequired = (voting as { account_required?: boolean }).account_required ?? false;
    this.accountVerified = (voting as { account_verified?: boolean }).account_verified ?? false;
    this.isOwner = (voting as { is_owner?: boolean }).is_owner ?? false;
    this.secret = (voting as { secret?: boolean }).secret;
    this.walletPolicy = (voting as { wallet_policy?: string }).wallet_policy;
    this.questionsPreview = (voting as { questions_preview?: { content: string; qtype: string; answers: string[] }[] }).questions_preview ?? [];
    this.signupsCount = (voting as { signups_count?: number }).signups_count ?? 0;
    this.privacyPolicyUrl = voting.privacy_policy_url
      ? `${process.env.REACT_APP_BACKEND_URL}${voting.privacy_policy_url}`
      : "";
    this.termsAndConditionsUrl = voting.terms_and_conditions_url
      ? `${process.env.REACT_APP_BACKEND_URL}${voting.terms_and_conditions_url}`
      : "";
    this.verifiedIdentityRequired = (voting as { verified_identity_required?: boolean }).verified_identity_required ?? false;
    this.minAssuranceLevel = (voting as { min_assurance_level?: number }).min_assurance_level ?? 2;
    this.loadCyberidAssertion();
  }

  // Wczytaj zapisaną asercję CyberID dla tego głosowania (po powrocie z weryfikacji) i prefiluj e-mail.
  loadCyberidAssertion() {
    if (!this.id) return;
    const stored = readCyberidAssertion(this.id);
    if (stored) {
      this.cyberidAssertion = stored.assertion;
      this.cyberidEmail = stored.email;
      this.email = stored.email;
    }
  }

  // Rozpocznij weryfikację tożsamości przez CyberID dla tego głosowania (redirect na CyberID).
  startVerification() {
    if (this.id) startCyberidVerify(this.id);
  }

  get cyberidVerified(): boolean {
    return !!this.cyberidAssertion;
  }

  public async getVoting(id: string, toast: ToastType, navigate: NavigateFunction, useEmail?: true) {
    runInAction(() => {
      this.id = id;
      this.isLoading = true;
      this.accessDenied = false;
    });
    const response = await getVotingDetails(id, useEmail && this.email);

    runInAction(() => {
      this.isLoading = false;
    });

    // Brak odpowiedzi = serwer nieosiągalny — interceptor przekieruje na /blad-serwera; nie crashuj na response.status.
    if (!response) return;

    const isUserNotAllowed = response.status === 403;

    if (this.isEmailNecessary && isUserNotAllowed && useEmail) {
      this.clearData();
      return navigate(`/glosowanie/${id}/nieuprawiony-email`);
    }

    // Zalogowany głosuje „jako konto" — jego e-mail jest już sprawdzony po stronie serwera.
    // 403 = po prostu brak dostępu (np. niepubliczne głosowanie, nie jest na liście) → czysty komunikat,
    // a NIE anonimowy formularz e-mail (ten jest tylko dla niezalogowanych zaproszonych).
    if (isUserNotAllowed && !useEmail && isLoggedIn()) {
      runInAction(() => {
        this.isLoading = false;
        this.accessDenied = true;
        this.isEmailNecessary = false;
      });
      return;
    }

    runInAction(() => {
      this.isLoading = false;
      this.isEmailNecessary = isUserNotAllowed;
    });
    if (response?.status === 200) {
      return this.saveVotingDetails(response.data);
    }

    !this.isEmailNecessary && this.showGenericErrorToast(toast);
  }

  validateQuestions() {
    this.questionErrors =
      this.questions
        ?.filter(question => !question.abstained && !(question.selectedAnswerIds?.length))
        .map(question => ({ questionId: question.id, message: "Zaznacz odpowiedź lub wstrzymaj się" })) ?? [];
  }

  validateEmail() {
    const rules = [
      { condition: !!this.email, message: "Podaj adres email" },
      {
        condition: !!this.email.match(GENERAL_MAIL_REGEX),
        message: "Nieprawidłowy format email"
      },
      {
        condition: this.email.length <= MAX_MAIL_SIGNS,
        message: `Maksymalna ilość znaków to ${MAX_MAIL_SIGNS}`
      }
    ];
    this.emailError = rules.find(rule => !rule.condition)?.message ?? "";
  }

  validateConsents() {
    const missingConsents = [
      this.consents.termsAndConditions,
      this.consents.privacyPolicy,
      this.consents.customTermsAndConditions || !this.termsAndConditionsUrl,
      this.consents.customPrivacyPolicy || !this.privacyPolicyUrl
    ].filter(consent => !consent);

    this.consentsError = missingConsents.length ? "Wymagana jest akceptacja wszystkich zgód" : "";
  }

  validate() {
    !this.isEmailNecessary && this.validateQuestions();
    !this.accountEligible && this.validateEmail();
    !this.isEmailNecessary && this.validateConsents();
  }

  clearData() {
    this.id = undefined;
    this.isPublic = undefined;
    this.organizer = undefined;
    this.name = undefined;
    this.startDate = undefined;
    this.endDate = undefined;
    this.questions = [];
    this.email = "";
    this.consents = {} as ConsentsType;
    this.consentsError = "";
    this.questionErrors = [];
    this.emailError = "";
    this.isLoading = false;
    this.isEmailNecessary = false;
    this.privacyPolicyUrl = "";
    this.termsAndConditionsUrl = "";
  }

  public handleSend(toast: ToastType, navigate: NavigateFunction) {
    return new Command(async () => {
      this.validate();
      if (this.isError) {
        return this.showGenericErrorToast(toast, FORM_ERROR_MESSAGE);
      }
      if (this.id) {
        if (this.isEmailNecessary) {
          return this.getVoting(this.id, toast, navigate, true);
        }

        // Weryfikacja tożsamości: głosowanie wymaga asercji CyberID, a wyborca się nie zweryfikował.
        if (this.verifiedIdentityRequired && !this.cyberidAssertion) {
          return this.showGenericErrorToast(toast, "Najpierw zweryfikuj tożsamość przez CyberID.");
        }

        // Tajne + WYMAGANY portfel → ceremonia (sekret w enklawie) musi powstać w aplikacji.
        // Przeglądarka nie ma enklawy → odsyłamy do apki (deep-link/QR), zamiast dowodzić token-only.
        if (this.secret && this.walletPolicy === "required") {
          runInAction(() => {
            this.showWalletHandoff = true;
          });
          return;
        }

        // Tajne głosowanie → ścieżka ZKP (dowód na urządzeniu, backend tylko przekazuje).
        // Uprawnienie sprawdza serwer przy enrollmencie (token/konto/Aura) — tu już tylko tajność.
        if (this.secret) {
          return this.castZkVote(toast, navigate);
        }

        const vote = (this.questions ?? []).flatMap(question =>
          question.abstained ? [] : (question.selectedAnswerIds ?? []).map(answer_id => ({ answer_id }))
        ) as VoteType;

        const response = await sendVote(this.id, {
          vote_answers_attributes: vote,
          voter_email: this.email,
          cyberid_assertion: this.cyberidAssertion
        });

        switch (response.status) {
          case 200: {
            const confirmed = (response.data as { confirmed?: boolean } | undefined)?.confirmed;
            if (confirmed) {
              toast({ title: "Twój głos został oddany ✓", status: "success", isClosable: true });
              runInAction(() => {
                this.accountVoted = true;
              });
              return;
            }
            navigate("/wyslanie-maila-z-potwierdzeniem-glosowania");
            return this.clearData();
          }

          case 403: {
            navigate(`/glosowanie/${this.id}/nieuprawiony-email`);
            return this.clearData();
          }

          case 422: {
            if (response.data?.errors?.voter_id?.[0] === HAS_ALREADY_VOTED_MESSAGE) {
              return this.showGenericErrorToast(
                toast,
                "Dla podanego adresu email został już oddany i potwierdzony głos"
              );
            }
          }
        }
      }
      return this.showGenericErrorToast(toast);
    });
  }

  // Tajny głos przez ZKP. Sekret żyje lokalnie; backend dostaje commitment, oddaje ścieżkę Merkle,
  // dowód powstaje na urządzeniu, a sam głos leci do węzła bez podpisu (autoryzacją jest dowód).
  // v1: tajne głosowania jednokrotnego wyboru (jeden zaznaczony wariant); wieloskładnikowe = TODO (circuit wektorowy).
  private async castZkVote(toast: ToastType, navigate: NavigateFunction) {
    if (!this.id) return;

    if (!isSnarkjsReady()) {
      return this.showGenericErrorToast(toast, "Moduł tajnego głosowania nie został załadowany. Odśwież stronę.");
    }

    const selected = (this.questions ?? []).flatMap(question =>
      question.abstained ? [] : question.selectedAnswerIds ?? []
    );
    if (selected.length !== 1) {
      return this.showGenericErrorToast(
        toast,
        "Tajne głosowanie obsługuje na razie pojedynczy wybór. Wieloskładnikowe — wkrótce."
      );
    }
    const voteValue = selected[0];

    try {
      const secret = getOrCreateZkSecret(this.id);
      const commitment = zkCommitment(secret);

      // 1. Enrollment + materiał (ASYNC): serwer autoryzuje wpis do drzewa i oddaje 202, dopóki liść nie wejdzie
      // do bloku; dopytujemy z backoffem (nie blokujemy web-workera). 403 = brak uprawnienia.
      const enroll = await this.enrollForZkVote(commitment, () =>
        toast({ title: "Zapisuję Twój anonimowy głos do rejestru — to potrwa chwilę…", status: "info", isClosable: true })
      );
      if (!enroll.ok && enroll.status === 403) {
        navigate(`/glosowanie/${this.id}/nieuprawiony-email`);
        return this.clearData();
      }
      if (!enroll.ok) {
        return this.showGenericErrorToast(toast);
      }

      // 2. Dowód LOKALNIE (na urządzeniu).
      const bundle = await proveZkVote(secret, enroll.material, voteValue);

      // 3. Relay do węzła.
      const response = await submitZkVote(this.id, bundle, this.email);
      switch (response?.status) {
        case 200: {
          toast({ title: "Twój anonimowy głos został oddany ✓", status: "success", isClosable: true });
          runInAction(() => {
            this.accountVoted = true;
          });
          return;
        }
        case 409: {
          return this.showGenericErrorToast(toast, "Z tego urządzenia oddano już głos w tym głosowaniu");
        }
        default:
          return this.showGenericErrorToast(toast);
      }
    } catch (err) {
      console.error(err);
      return this.showGenericErrorToast(toast);
    }
  }

  // Enrollment ASYNC do drzewa elektoratu. Backend przy pierwszym żądaniu autoryzuje wpis (idempotentnie) i oddaje
  // 202 „pending", dopóki liść nie wejdzie do bloku; kolejne odpytania tylko czekają. Dopytujemy z backoffem
  // (retry_after z ciała), więc czekanie jest po stronie klienta, nie web-workera. onPending odpala się raz, gdy
  // enrollment faktycznie trwa (nie przy natychmiastowym 200), żeby pokazać wyborcy komunikat o oczekiwaniu.
  private async enrollForZkVote(
    commitment: string,
    onPending?: () => void
  ): Promise<{ ok: true; material: ZkMaterial } | { ok: false; status: number }> {
    const MAX_ATTEMPTS = 15; // ~do 45 s przy retry_after=3
    let notifiedPending = false;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const res = await getZkVoteMaterial(this.id!, commitment, this.email, this.cyberidAssertion);
      const status = res?.status ?? 0;
      if (status === 200 && res?.data) return { ok: true, material: res.data as ZkMaterial };
      if (status !== 202) return { ok: false, status }; // 403/409/5xx itd. → obsłuż w wywołującym
      if (!notifiedPending) {
        notifiedPending = true;
        onPending?.();
      }
      const retryAfter = Number(res?.data?.retry_after) || 3;
      await new Promise(resolve => setTimeout(resolve, retryAfter * 1000));
    }
    return { ok: false, status: 202 }; // timeout oczekiwania → potraktuj jak błąd „spróbuj ponownie"
  }

  closeWalletHandoff() {
    runInAction(() => {
      this.showWalletHandoff = false;
    });
  }

  // Universal/App Link do apki — z `?wallet=1` apka idzie wprost do ceremonii tajnego głosu.
  get handoffUrl(): string {
    return `${process.env.REACT_APP_URL ?? ""}/glosowanie/${this.id ?? ""}?wallet=1`;
  }

  get isError(): boolean {
    return !!this.emailError || !!this.consentsError || !!this.questionErrors.length;
  }

  override async onInit() {}
}
